import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import apiClient from "../../../api/client";
import PhoneVerificationModal from "../../../components/phone-verification-modal";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../../components/ui/input-otp";
import { useApp as portalApp } from '../../../context/usePortal';

enum PinUpdateFlowStep {
  EnterNewPin,
  ConfirmPin,
  Success
}

const formSchema = z.object({
  newPin: z.string().min(6, "PIN must be at least 6 characters"),
  confirmPin: z.string().min(6, "PIN must be at least 6 characters"),
});

type FormSchema = z.infer<typeof formSchema>;

const ResetPin: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [verifyPhone, setVerifyPhone] = useState<boolean>(false);
  const { resetPIN, user } = portalApp()
  const [step, setStep] = useState(PinUpdateFlowStep.EnterNewPin);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPin: "",
      confirmPin: "",
    },
  });

  const phoneNumber = `${user?.country_code}-${user?.user_phone_number}`;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setStep(PinUpdateFlowStep.EnterNewPin);
    setVerifyPhone(false);
    form.reset();
  }

  const handleNewPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }


    setStep(PinUpdateFlowStep.ConfirmPin);
  }

  const handleConfirmPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }

    onSubmit(form.getValues());
  }

  const handleStartAgain = () => {
    form.reset();
    setStep(PinUpdateFlowStep.EnterNewPin);
  }

  const onSubmit = (data: FormSchema) => {
    if (data.newPin !== data.confirmPin) {
      form.setError("confirmPin", {
        type: "manual",
        message: "PINs do not match",
      });
      return;
    }

    setVerifyPhone(true);
  }

  const resetPin = useMutation({
    mutationFn: async ({
      newPin,
      otp,
    }: {
        newPin: string,
        otp: string,
      }) => {
      await apiClient.post('/users/reset-pin', {
        newPin,
        otp
      });
    },
    onSuccess: () => {
      toast.success("PIN reset successfully");

      setVerifyPhone(false);
      setStep(PinUpdateFlowStep.Success);
    },
    onError: (error) => {
      console.error("Error resetting PIN:", error);
      toast.error((error as any)?.response?.data?.error?.message || "Failed to reset PIN. Please try again.");
    }
  })

  const handleSubmitChangePin = (otp: string) => {
    resetPin.mutate({
      newPin: form.getValues().newPin,
      otp,
    });
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-t-1 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-gray-900">
            Reset PIN
          </h3>
          <p className="text-sm text-gray-500">
            Forgot your PIN? Reset here to continue using
            your AyeFace account for payment
          </p>
          <p className="text-sm italic text-gray-500">
            PIN last updated: July 13, 2024
          </p>
        </div>
        <Button
          type="button"
          className="min-w-24"
          onClick={() => {
            setOpenDialog(true);
          }}
        >
          Reset PIN
        </Button>
      </div>
      
      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          <DialogHeader className='mt-5'>
            <DialogTitle className="text-center">
              {step === PinUpdateFlowStep.EnterNewPin
                ? "Please Enter New 6-Digit PIN"
                : step === PinUpdateFlowStep.ConfirmPin && "Enter New PIN Again"
              }
            </DialogTitle>
          </DialogHeader>

          {step !== PinUpdateFlowStep.Success ? (
            <>
              <p className='text-sm text-foreground/90 text-center'>
                Please {step === PinUpdateFlowStep.EnterNewPin ? "" : "re-"}enter your 6-digit PIN
              </p>

              <form className="flex flex-col items-center mb-10">
                {step === PinUpdateFlowStep.EnterNewPin && (
                  <>
                    <Controller
                      name={"newPin"}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleNewPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.confirmPin && (
                      <span className="text-xs text-red-500 mt-2">
                        {form.formState.errors.confirmPin.message as string}
                      </span>
                    )}
                  </>
                )}

                {step === PinUpdateFlowStep.ConfirmPin && (
                  <>
                    <Controller
                      name={"confirmPin"}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleConfirmPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.confirmPin && (
                      <span className="text-xs text-red-500 mt-2">
                        {form.formState.errors.confirmPin.message as string}
                      </span>
                    )}
                  </>

                )}
              </form>
              
              {step === PinUpdateFlowStep.ConfirmPin && (
                <Button variant={'link'} className='decoration-[#5B5959]' onClick={handleStartAgain}>
                  <p className='text-[#5B5959]'>
                    Start Again?
                  </p>
                </Button>
              )}
            </>
          ) : (
          <>
            <img
              src="/assets/images/update-phone-success.png"
              alt="Phone Update Success"
              className='w-32 mx-auto'
            />
                
            <h3 className='text-center text-md font-semibold'>Your PIN has been updated successfully</h3>

            <Button
              className='bg-[#5CB891] hover:bg-[#5CB891] mt-6 rounded-full'
              onClick={handleCloseDialog}
              type='button'
            >
              Done
            </Button>
          </>
          )}

        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={phoneNumber}
        onClose={() => {
          setVerifyPhone(false);
        }}
        onVerify={otp => {
          handleSubmitChangePin(otp);
        }}
        isOpen={verifyPhone}
      />
    </>
  )
}

export default ResetPin